<template>
  <div style="height: 100%">
    <list-form
      ref="list-form"
      :config="config"
      :formModel="formModel"
    >
      <template v-slot="{ data, item, autoSave }">
        <activity-item
          :displayActivityLog="displayActivityLog"
          :data="data"
          :item="item"
          :formModel="formModel"
          :autoSave="autoSave"
          @onStockCard="handlerStockCard"
        >
          <template v-slot:user-actions>
            <button type="button" class="btn btn-size-s btn-mobile-full" @click="handlerConnectPO(data)">
              {{L10n('open-po')}}
            </button>
            <button type="button" class="btn btn-size-s btn-mobile-full" @click="handlerConnectWO(data)">
              {{L10n('open-wo')}}
            </button>
            <button type="button" class="btn btn-size-s btn-mobile-full" @click="handleGoToCustomerPoOpen(data)">
              {{L10n('go-to-customer-po-open')}}
            </button>
          </template>
        </activity-item>
      </template>
    </list-form>
    <dialog-status-repair-order />
    <dialog-new-instruction-repair-order />
    <dialog-status-customer />
    <dialog-new-instruction-customer />
    <dialog-stock-card
      ref="dialog-stock-card"
    />
    <dialog-connect-po
      ref="dialog-connect-po"
    />
    <dialog-connect-wo
      ref="dialog-connect-wo"
    />
  </div>
</template>

<script>
import { config as listFormConfig } from './ListFormConfig.js'
import { waitValue } from '@/utils/promises.js'
import { MainModel } from '@/models/CustomerPOFollowUp'
import * as dynamic from '@/utils/dynamic'
import { ServiceCustomerPOFollowUp } from '@/services/CustomerPOFollowUp'
import ExcelExporter from '@/components/ExcelExporter'

export default {
  name: 'ListFormSupplierQuotes',
  components: {
    'list-form': () => import(/* webpackChunkName: 'ListForm' */ '@/components/ListForm/Index'),
    'dialog-status-repair-order': () => import(/* webpackChunkName: 'DialogStatusRepairOrder' */ '@/components/Shared/ActivityDialogStatusEmailRepairOrder'),
    'dialog-new-instruction-repair-order': () => import(/* webpackChunkName: 'DialogNewInstructionRepairOrder' */ '@/components/Shared/ActivityDialogNewInstructionRepairOrder'),
    'dialog-new-instruction-customer': () => import(/* webpackChunkName: 'DialogNewInstructionCustomer' */ '@/components/Shared/ActivityDialogNewInstructionCustomer'),
    'dialog-status-customer': () => import(/* webpackChunkName: 'ActivityDialogStatusEmailCustomer' */ '@/components/Shared/ActivityDialogStatusEmailCustomer'),
    'activity-item': () => import(/* webpackChunkName: 'Shared' */ '@/components/Shared/ActivityItemCustomerPoFollowUp/Index'),
    'dialog-stock-card': () => import(/* webpackChunkName: 'DialogStockCard' */ '@/components/Shared/Activity/DialogStockCard'),
    'dialog-connect-po': () => import(/* webpackChunkName: 'DialogConnectPo' */ '@/components/Shared/ActivityDialogConnectToOpenPO/Index'),
    'dialog-connect-wo': () => import(/* webpackChunkName: 'DialogConnectWo' */ '@/components/Shared/ActivityDialogConnectToOpenWO/Index')
  },
  created () {
    this.$bus.$on('customer-po-followup-on-filter', this.handlerFilter)
    this.$bus.$on('customer-po-followup-on-export', this.handlerExport)
  },
  beforeDestroy () {
    this.$bus.$off('customer-po-followup-on-filter', this.handlerFilter)
    this.$bus.$off('customer-po-followup-on-export', this.handlerExport)
  },
  props: {
    orderBy: { type: Number, required: true },
    displayActivityLog: { type: Boolean, required: true }
  },
  watch: {
    orderBy () {
      this.getListForm()
        .then(list => list.loadData())
    }
  },
  computed: {
    config () {
      return listFormConfig(this.orderBy)
    }
  },
  methods: {
    formModel () {
      return new MainModel()
    },
    getListForm () {
      return waitValue(() => this.$refs['list-form'])
    },
    handlerFilter (filterValue) {
      this.getListForm()
        .then((list) => {
          list.setFilters(filterValue)
          list.loadData()
        })
    },
    handleGoToCustomerPoOpen ({ rfqID }) {
      const { href } = this.$router.resolve({ name: 'CustomerPoOpen', query: { 'customer-po-open': `edit[${rfqID}]` } })
      window.open(href, '_blank')
    },
    handlerConnectPO ({ rfqid }) {
      this.$refs['dialog-connect-po'].show({ rfqid })
    },
    handlerConnectWO ({ rfqid }) {
      this.$refs['dialog-connect-wo'].show({ rfqid })
    },
    handlerStockCard () {
      this.$refs['dialog-stock-card'].show(...arguments)
    },
    async handlerExport ({ isAll }) {
      const list = await this.getListForm()

      list && list.setLoading(true)

      try {
        const { params } = list.createGetRequest()

        const { data: { value: records } } = await ServiceCustomerPOFollowUp.getAllDynamic({
          ...params,
          select: dynamic
            .select(
              'rfq3.rfq.customerCompany.name as customerCompanyName', 'rfq3.rfq.requestedDate as requestedDate',
              'rfq3.coreDueDate', 'po', 'part.partNumber', 'partSN', 'rfq3.price', 'rfq3.currency.title as currencyTitle',
              'rfq3.condition.title as conditionTitle', 'rfq3.quoteDate', 'rfq3.supplyDate',
              'rfq3.tagInfoCompany.name as tagInfoCompanyName',
              'rfq3.tagDate',
              'rfq3.traceabilityCompany.name as traceabilityCompanyName',
              'rfq3.remarks', 'rfq3.exchangeCoreValue',
              'rfq3.exchangeCoreValueCurrency.title as exchangeCoreValueCurrencyTitle',
              'rfq3.exchangeCondition.title as exchangeConditionTitle',
              'rfq3.rfq.customerContact.name as customerContactName',
              'rfq3.qty',
              'rfq3.rfq.observation',
              'rfq3.exchangeCoreReceived',
              'rfq3.rfq.rfqType.title as rfqTypeTitle'),
          ...(isAll ? { take: undefined, skip: undefined } : {})
        })

        const columns = [
          { title: this.L10n('customer'), field: 'customerCompanyName' },
          { title: this.L10n('requested-date'), field: 'requestedDate', type: 'd' },
          { title: this.L10n('rfq-type'), field: 'rfqTypeTitle' },
          { title: this.L10n('core-due-date'), field: 'coreDueDate', type: 'd' },
          { title: this.L10n('po'), field: 'po' },
          { title: this.L10n('part-number'), field: 'partNumber' },
          { title: this.L10n('part-SN'), field: 'partSN' },
          { title: this.L10n('po'), field: 'po' },
          { title: this.L10n('price'), field: 'price' },
          { title: this.L10n('currency'), field: 'currencyTitle' },
          { title: this.L10n('condition'), field: 'conditionTitle' },
          { title: this.L10n('quote-date'), field: 'quoteDate', type: 'd' },
          { title: this.L10n('supply-date'), field: 'supplyDate' },
          { title: this.L10n('tag-info-company'), field: 'tagInfoCompanyName' },
          { title: this.L10n('tag-date'), field: 'tagDate', type: 'd' },
          { title: this.L10n('traceability-company'), field: 'traceabilityCompanyName' },
          { title: this.L10n('remarks'), field: 'remarks' },
          { title: this.L10n('exchange-core-value'), field: 'exchangeCoreValue' },
          { title: this.L10n('exchange-core-value-currency'), field: 'exchangeCoreValueCurrencyTitle' },
          { title: this.L10n('exchange-condition'), field: 'exchangeConditionTitle' },
          { title: this.L10n('customer-contact'), field: 'customerContactName' },
          { title: this.L10n('qty'), field: 'qty' },
          { title: this.L10n('observation'), field: 'observation' },
          { title: this.L10n('exchange-core-received'), field: 'exchangeCoreReceived', type: 'b' }
        ].map((column) => ({
          ...column,
          width: Math.max(column.title.length, ...records.map((record) => record[column.field] ? String(record[column.field]).length : 0))
        }))

        await ExcelExporter.export(this.L10n('customer-po-follow-up'), columns, records)
      } catch (e) {
        this.requestErrorHandler(e)
      } finally {
        list && list.setLoading(false)
      }
    }
  }
}
</script>
